import { DT } from '../../theme/designTokens';
import { CountryFlag } from '../ui/CountryFlag';
import { useTranslation } from '../../i18n/react';

/**
 * MatchCard — the MATCH section of the game info panel, per the GAMESCENE
 * Figma: a gold-bordered card with the match title, a 0–100 progress bar,
 * the big score, and Team A / Team B labels.
 *
 * Reused by both the desktop info column and the mobile stacked layout.
 */

// Figma team label colours (distinct from DT.color.teamRed/teamBlue, which are
// the brighter in-canvas tokens). These match the panel text exactly.
export const TEAM_A_COLOR = '#CD1B1B';
export const TEAM_B_COLOR = '#2371C0';
const PROGRESS_FILL = '#009216';

export const PANEL_LABEL: React.CSSProperties = {
  fontFamily:     DT.font.sans,
  fontWeight:     700,
  fontSize:       13,
  letterSpacing:  '0.12em',
  textTransform:  'uppercase',
  color:          DT.color.gold,
};

const CARD: React.CSSProperties = {
  boxSizing:    'border-box',
  background:   DT.color.cardBg,
  border:       `1px solid ${DT.color.gold}`,
  borderRadius: 16,
};

interface Props {
  /** The viewer's own display name — e.g. "LuisGeronim" in "LuisGeronim Table to 100". */
  playerName: string;
  /** The viewer's own country, null/unset → neutral globe. */
  countryCode?: string | null;
  /** Semantic marker: this table belongs to the local viewer with no real
   *  account identity behind it (the scripted tutorial). The title renders
   *  the self-table sentence ("Your table · N points" / "Tu mesa · N puntos")
   *  instead of interpolating a display name. Production games never set it. */
  viewerTable?: boolean;
  target:    number;   // e.g. 100
  usScore:   number;
  themScore: number;
}

export function MatchCard({ playerName, countryCode, viewerTable, target, usScore, themScore }: Props) {
  const { t } = useTranslation('gameplay');
  const lead    = Math.max(usScore, themScore);
  const pct      = Math.max(0, Math.min(1, target > 0 ? lead / target : 0));

  return (
    <div>
      <div style={{ ...PANEL_LABEL, marginBottom: 12 }}>{t('match.label')}</div>

      <div style={{ ...CARD, padding: '16px 20px' }}>
        {/* Title — the viewer's own identity genuinely appears here, so it
            carries its own flag, restructured out of the plain sentence. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: DT.font.sans, fontSize: 15, color: DT.color.textPrimary, marginBottom: 14 }}>
          <CountryFlag countryCode={countryCode} width={16} />
          <span>{viewerTable ? t('match.yourTableTo', { target }) : t('match.tableTo', { name: playerName, target })}</span>
        </div>

        {/* Progress bar 0 … target */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span style={{ fontFamily: DT.font.sans, fontSize: 13, color: DT.color.textPrimary }}>0</span>
          <div style={{ position: 'relative', flex: 1, height: 8, background: '#FFFFFF', borderRadius: 16 }}>
            <div
              style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${pct * 100}%`, minWidth: pct > 0 ? 8 : 0,
                background: PROGRESS_FILL, borderRadius: 16,
              }}
            />
          </div>
          <span style={{ fontFamily: DT.font.sans, fontSize: 13, color: DT.color.textPrimary }}>{target}</span>
        </div>

        {/* Score */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
          <Score value={usScore} />
          <span style={{ fontFamily: DT.font.heading, fontSize: 34, fontWeight: 600, color: DT.color.gold, lineHeight: 1 }}>–</span>
          <Score value={themScore} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginTop: 6 }}>
          <TeamLabel color={TEAM_A_COLOR} label={t('team.a')} />
          <span style={{ width: 20 }} />
          <TeamLabel color={TEAM_B_COLOR} label={t('team.b')} />
        </div>
      </div>
    </div>
  );
}

function Score({ value }: { value: number }) {
  return (
    <span style={{ fontFamily: DT.font.heading, fontSize: 40, fontWeight: 600, color: DT.color.gold, lineHeight: 1, minWidth: 44, textAlign: 'center' }}>
      {value}
    </span>
  );
}

function TeamLabel({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ fontFamily: DT.font.sans, fontSize: 14, color, minWidth: 44, textAlign: 'center' }}>
      {label}
    </span>
  );
}
